import { RefetchFunction } from '@apollo/client/react/hooks/useSuspenseQuery';
import { FC, useEffect } from 'react';
import { gql } from '@/__generated__';
import {
  StreamPostCardFragmentFragment,
  StreamPostsQueryQuery,
} from '@/__generated__/graphql.ts';
import { updateFeedCache } from '@/apollo-cache-utils/update-feed-cache.ts';
import { useSubscription } from '@/apollo/use-subscription.ts';
import { ConnectionStatusType } from '@/components/connection-status/connection-status-type.ts';
import { StreamPostCardFragment } from '@/components/stream-post-card/stream-post-card-fragment.ts';
import { getStreamNotifSoundStatus } from '@/components/streams/toggle-notif-sound/stream-notif-sound-status.ts';
import { getStreamSoundSettings } from '@/components/streams/toggle-notif-sound/stream-sound-settings.ts';
import { SoundStatus } from '@/components/toggle-sound/sound-status.ts';
import { playNotificationAudio } from '@/utils/audio-utils/play-notification-audio.ts';
import { stopReadAloud } from '@/utils/audio-utils/read-aloud.ts';

const StreamPostsSubscription = gql(`
subscription OnStreamPostsUpdated($streamId: ID!) {
  streamPostsUpdates(streamId: $streamId) {
    ...StreamPostCardFragment
  }
}
`);

export interface StreamSubscriptionProps {
  streamId: string;
  onData: (post: NonNullable<StreamPostCardFragmentFragment>) => void;
  setSubscriptionStatus: (subscriptionStatus: ConnectionStatusType) => void;
  refetchQuery: RefetchFunction<StreamPostsQueryQuery, Record<string, unknown>>;
}

export const StreamSubscription: FC<StreamSubscriptionProps> = ({
  streamId,
  onData,
  setSubscriptionStatus,
  refetchQuery,
}) => {
  useEffect(() => () => stopReadAloud(streamId), [streamId]);

  useSubscription(
    StreamPostsSubscription,
    {
      variables: { streamId },
      onData: async ({ data, client }) => {
        const post = data?.data?.streamPostsUpdates;
        if (post) {
          onData(post);

          client.cache.modify({
            fields: {
              streamPosts(existing) {
                const newPostRef = client.cache.writeFragment({
                  fragment: StreamPostCardFragment,
                  fragmentName: 'StreamPostCardFragment',
                  data: post,
                });

                return updateFeedCache(existing, newPostRef);
              },
            },
          });

          if (getStreamNotifSoundStatus(streamId) === SoundStatus.on) {
            const settings = getStreamSoundSettings(streamId);
            await playNotificationAudio({
              track: settings.track,
              volume: settings.volume,
              text: post.text || '',
              ownerId: streamId,
            });
          }
        }
      },
    },
    setSubscriptionStatus,
    refetchQuery,
  );

  return null;
};
